import type {
  AssignUserCostCenterParams,
  AssignUserCostCenterResponse,
  CostCenterHistoryEntry,
  CostCenterIdResponse,
  CostCenterInconsistenciesResponse,
  CreateCostCenterParams,
  CreateCostCenterResponse,
  GetCostCenterParams,
  GetCostCenterResponse,
  ListCostCentersParams,
  ListCostCentersResponse,
  SetActiveCostCenterParams,
  UpdateCostCenterParams,
} from '@src/models/interfaces/costCenter.interface';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

/**
 * Cost centers are written only through `costCenters.*` callables (Admin SDK).
 * List/get also go through the API so responsible-user scoping stays on the server.
 */
class CostCenterRepository {
  async list(params: ListCostCentersParams): Promise<ListCostCentersResponse> {
    const fn = apiCallable<ListCostCentersParams, ListCostCentersResponse>(
      API_OPERATIONS.COST_CENTERS_LIST,
    );
    const response = await fn(params);
    return response.data;
  }

  async get(params: GetCostCenterParams): Promise<GetCostCenterResponse> {
    const fn = apiCallable<GetCostCenterParams, GetCostCenterResponse>(
      API_OPERATIONS.COST_CENTERS_GET,
    );
    const response = await fn(params);
    return response.data;
  }

  async create(params: CreateCostCenterParams): Promise<string> {
    const fn = apiCallable<CreateCostCenterParams, CreateCostCenterResponse>(
      API_OPERATIONS.COST_CENTERS_CREATE,
    );
    const response = await fn(params);
    return response.data.id;
  }

  async update(params: UpdateCostCenterParams): Promise<string> {
    const fn = apiCallable<UpdateCostCenterParams, CostCenterIdResponse>(
      API_OPERATIONS.COST_CENTERS_UPDATE,
    );
    const response = await fn(params);
    return response.data.id;
  }

  async setActive(params: SetActiveCostCenterParams): Promise<string> {
    const fn = apiCallable<SetActiveCostCenterParams, CostCenterIdResponse>(
      API_OPERATIONS.COST_CENTERS_SET_ACTIVE,
    );
    const response = await fn(params);
    return response.data.id;
  }

  async inconsistencies(organizationId: string): Promise<CostCenterInconsistenciesResponse> {
    const fn = apiCallable<{ organizationId: string }, CostCenterInconsistenciesResponse>(
      API_OPERATIONS.COST_CENTERS_INCONSISTENCIES,
    );
    const response = await fn({ organizationId });
    return response.data;
  }

  async assignUser(params: AssignUserCostCenterParams): Promise<AssignUserCostCenterResponse> {
    const fn = apiCallable<AssignUserCostCenterParams, AssignUserCostCenterResponse>(
      API_OPERATIONS.COST_CENTERS_ASSIGN_USER,
    );
    const response = await fn(params);
    return response.data;
  }

  async listUserHistory(
    organizationId: string,
    userId: string,
  ): Promise<{ entries: CostCenterHistoryEntry[] }> {
    const fn = apiCallable<
      { organizationId: string; userId: string },
      { entries: CostCenterHistoryEntry[] }
    >(API_OPERATIONS.COST_CENTERS_USER_HISTORY);
    const response = await fn({ organizationId, userId });
    return response.data;
  }
}

export default CostCenterRepository;
